/**
 * Donors / donations (staff view). Captured-donation totals header + a filterable list
 * of donations. Contactable for internal thank-you only (no political reuse — §13).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Heart, RefreshCw, Mail, Plus } from 'lucide-react';
import { api } from '../lib/apiClient.js';
import { useToast } from '../components/feedback/ToastProvider.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { Field, TextInput, Select } from '../components/ui/Field.jsx';
import { formatINR, formatDate, CATEGORY_META } from '../features/cases/caseMeta.js';
import styles from './Donors.module.css';

const STATUS = { created: 'Created', captured: 'Captured', failed: 'Failed' };

export default function Donors() {
  const [status, setStatus] = useState('captured');
  const [state, setState] = useState({ status: 'loading', items: [], totals: null, nextCursor: null, hasMore: false });
  const [showRecord, setShowRecord] = useState(false);
  const nextCursorRef = useRef(null);

  const load = useCallback(
    async (append = false) => {
      const cursor = append ? nextCursorRef.current : null;
      setState((s) => ({ ...s, status: append ? s.status : 'loading' }));
      try {
        const params = new URLSearchParams({ limit: '30' });
        if (status) params.set('status', status);
        if (cursor) params.set('cursor', cursor);
        const env = await api.getEnvelope(`/donations?${params}`);
        nextCursorRef.current = env.meta.page.nextCursor;
        setState((s) => ({
          status: 'ready',
          items: append ? [...s.items, ...env.data] : env.data,
          totals: env.meta.totals,
          nextCursor: env.meta.page.nextCursor,
          hasMore: env.meta.page.hasMore,
        }));
      } catch (err) {
        setState({ status: 'error', items: [], error: err.message });
      }
    },
    [status],
  );

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Supporters</p>
          <h1 className={styles.h1}>Donors &amp; donations</h1>
        </div>
        <div className={styles.headActions}>
          <button className={styles.ghost} onClick={() => load(false)}>
            <RefreshCw size={15} /> Refresh
          </button>
          <button className={styles.primary} onClick={() => setShowRecord(true)}>
            <Plus size={16} /> Record donation
          </button>
        </div>
      </header>

      <div className={styles.totals}>
        <div className={styles.totalCard}>
          <span className={styles.totalLabel}><Heart size={14} /> Total received</span>
          <span className={`${styles.totalValue} mono`}>{state.totals ? formatINR(state.totals.total) : '—'}</span>
          <span className={styles.totalSub}>{state.totals ? `${state.totals.count} captured donations` : ''}</span>
        </div>
      </div>

      <div className={styles.toolbar}>
        {Object.entries(STATUS).map(([k, label]) => (
          <button key={k} className={`${styles.chip} ${status === k ? styles.chipOn : ''}`} onClick={() => setStatus(k)}>
            {label}
          </button>
        ))}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th className={styles.right}>Amount</th>
              <th>Allocation</th>
              <th>Email</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {state.status === 'loading' &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}><td colSpan={5}><div className={styles.skeleton} /></td></tr>
              ))}
            {state.status === 'ready' && state.items.length === 0 && (
              <tr><td colSpan={5} className={styles.empty}>No donations in this filter.</td></tr>
            )}
            {state.status === 'ready' &&
              state.items.map((d) => (
                <tr key={d.id}>
                  <td className={styles.sub}>{formatDate(d.at)}</td>
                  <td className={`${styles.right} ${styles.amount} mono`}>{formatINR(d.amount)}</td>
                  <td>{d.allocationCategory ? CATEGORY_META[d.allocationCategory]?.label : 'General'}</td>
                  <td>
                    {d.donorEmail ? (
                      <a href={`mailto:${d.donorEmail}`} className={styles.email}><Mail size={13} /> {d.donorEmail}</a>
                    ) : (
                      <span className={styles.sub}>Anonymous</span>
                    )}
                  </td>
                  <td><span className={`${styles.status} ${styles[d.status]}`}>{STATUS[d.status]}</span></td>
                </tr>
              ))}
          </tbody>
        </table>
        {state.hasMore && state.status === 'ready' && (
          <button className={styles.loadMore} onClick={() => load(true)}>Load more</button>
        )}
      </div>

      <RecordDonationModal
        open={showRecord}
        onClose={() => setShowRecord(false)}
        onRecorded={() => {
          setShowRecord(false);
          setStatus('captured');
          load(false);
        }}
      />
    </div>
  );
}

const EMPTY_DONATION = { donorName: '', donorEmail: '', donorPhone: '', amount: '', allocationCategory: '', message: '' };

function RecordDonationModal({ open, onClose, onRecorded }) {
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_DONATION);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [enquiries, setEnquiries] = useState([]);
  const [fromEnquiry, setFromEnquiry] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Load enquiry people (pledges first) so a recorded donation can be tied to someone
  // who already contacted us.
  useEffect(() => {
    if (!open) return;
    api
      .get('/enquiries?limit=100')
      .then((list) => {
        const sorted = [...list].sort(
          (a, b) => (b.topic === 'donation' ? 1 : 0) - (a.topic === 'donation' ? 1 : 0),
        );
        setEnquiries(sorted);
      })
      .catch(() => setEnquiries([]));
  }, [open]);

  const prefillFrom = (id) => {
    setFromEnquiry(id);
    const e = enquiries.find((x) => x.id === id);
    if (!e) return;
    const amt = (e.subject?.match(/₹\s*([\d,]+)/) || [])[1]?.replace(/,/g, '') || '';
    setForm((f) => ({
      ...f,
      donorName: e.name || f.donorName,
      donorEmail: e.email || f.donorEmail,
      donorPhone: e.phone || f.donorPhone,
      amount: amt || f.amount,
      message: f.message || e.subject || '',
    }));
  };

  const reset = () => {
    setForm(EMPTY_DONATION);
    setFromEnquiry('');
    setErrors({});
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!(Number(form.amount) >= 1)) errs.amount = 'Enter an amount';
    if (form.donorEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.donorEmail.trim())) errs.donorEmail = 'Invalid email';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    try {
      await api.post('/donations/record', {
        donorName: form.donorName.trim() || undefined,
        donorEmail: form.donorEmail.trim() || undefined,
        donorPhone: form.donorPhone.trim() || undefined,
        amount: Number(form.amount),
        allocationCategory: form.allocationCategory || undefined,
        message: form.message.trim() || undefined,
      });
      // If this came from an enquiry, mark that enquiry resolved (the pledge is fulfilled).
      if (fromEnquiry) {
        api.patch(`/enquiries/${fromEnquiry}`, { status: 'resolved' }).catch(() => {});
      }
      toast.success('Donation recorded.');
      reset();
      onRecorded();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Record a donation" width={480}>
      <form onSubmit={submit} id="record-donation-form">
        {enquiries.length > 0 && (
          <Field label="Prefill from an enquiry" hint="Optional — pick someone who contacted you">
            <Select value={fromEnquiry} onChange={(e) => prefillFrom(e.target.value)}>
              <option value="">— None (enter manually) —</option>
              {enquiries.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}{e.topic === 'donation' ? ' · pledge' : ` · ${e.topic}`}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Amount (₹)" required error={errors.amount}>
          <TextInput type="number" min="1" value={form.amount} onChange={set('amount')} placeholder="2500" />
        </Field>
        <Field label="Donor name" hint="Optional">
          <TextInput value={form.donorName} onChange={set('donorName')} placeholder="Gurpreet Kaur" />
        </Field>
        <Field label="Donor email" hint="Optional — unlocks their donor dashboard" error={errors.donorEmail}>
          <TextInput type="email" value={form.donorEmail} onChange={set('donorEmail')} placeholder="donor@example.com" />
        </Field>
        <Field label="Donor phone" hint="Optional">
          <TextInput value={form.donorPhone} onChange={set('donorPhone')} placeholder="+91…" />
        </Field>
        <Field label="Allocate to" hint="Optional">
          <Select value={form.allocationCategory} onChange={set('allocationCategory')}>
            <option value="">Where needed most</option>
            {Object.entries(CATEGORY_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </Select>
        </Field>
        <Field label="Note" hint="Optional">
          <TextInput value={form.message} onChange={set('message')} placeholder="e.g. bank transfer ref / in memory of…" />
        </Field>
      </form>
      <div className={styles.modalActions}>
        <button className={styles.ghost} onClick={onClose} type="button">Cancel</button>
        <button className={styles.primary} form="record-donation-form" type="submit" disabled={busy}>
          {busy ? 'Recording…' : 'Record donation'}
        </button>
      </div>
    </Modal>
  );
}
